/**
 * Android Bluetooth Bridge
 * ========================
 * Provides a Promise-based interface to native Android BLE functionality
 * when running inside an Android WebView that has injected an
 * `AndroidBluetooth` object via `addJavascriptInterface()`.
 *
 * ─── Android App Integration Guide ────────────────────────────────
 *
 * In your AndroidManifest.xml, add these permissions:
 *   <uses-permission android:name="android.permission.BLUETOOTH" />
 *   <uses-permission android:name="android.permission.BLUETOOTH_ADMIN" />
 *   <uses-permission android:name="android.permission.BLUETOOTH_SCAN" />
 *   <uses-permission android:name="android.permission.BLUETOOTH_CONNECT" />
 *   <uses-permission android:name="android.permission.ACCESS_FINE_LOCATION" />
 *
 * In your Activity/Fragment where you set up WebView:
 *
 *   webView.settings.javaScriptEnabled = true
 *   webView.addJavascriptInterface(AndroidBluetoothInterface(this, webView), "AndroidBluetooth")
 *
 * Then implement the `AndroidBluetoothInterface` class in Kotlin/Java with
 * `@JavascriptInterface` on each method (see interface spec below).
 *
 * ─── Required Native Methods (@JavascriptInterface) ───────────────
 *
 * ```kotlin
 * class AndroidBluetoothInterface(
 *     private val context: Context,
 *     private val webView: WebView
 * ) {
 *
 *   // Check if Bluetooth is enabled
 *   @JavascriptInterface
 *   fun isBluetoothEnabled(): Boolean { ... }
 *
 *   // Scan for BLE devices.
 *   // filter: "printer" | "scale" | "all"
 *   // callbackId: unique string, call window.__btCallback(callbackId, devicesJson) when done
 *   // devicesJson: JSON array of { id, name, rssi, type }
 *   @JavascriptInterface
 *   fun scanDevices(filter: String, callbackId: String) { ... }
 *
 *   // Connect to a device by its MAC address/ID
 *   // callbackId: call window.__btCallback(callbackId, resultJson)
 *   // resultJson: { success: true, id, name } | { success: false, error: "..." }
 *   @JavascriptInterface
 *   fun connectDevice(deviceId: String, deviceType: String, callbackId: String) { ... }
 *
 *   // Disconnect device
 *   @JavascriptInterface
 *   fun disconnectDevice(deviceId: String) { ... }
 *
 *   // Write base64-encoded bytes to connected printer
 *   // callbackId: call window.__btCallback(callbackId, resultJson)
 *   // resultJson: { success: true } | { success: false, error: "..." }
 *   @JavascriptInterface
 *   fun writePrinter(deviceId: String, base64Data: String, callbackId: String) { ... }
 *
 *   // Register a JS callback name for weight streaming
 *   // JS side will receive: window[callbackName]({ weight, unit, stable })
 *   @JavascriptInterface
 *   fun startWeightStream(deviceId: String, callbackName: String) { ... }
 *
 *   @JavascriptInterface
 *   fun stopWeightStream(deviceId: String) { ... }
 * }
 * ```
 *
 * ─── Example Native Callback Pattern ──────────────────────────────
 *
 * ```kotlin
 * // In your Kotlin code, to call back into JS:
 * fun sendToJs(callbackId: String, result: String) {
 *     webView.post {
 *         webView.evaluateJavascript("window.__btCallback('$callbackId', $result)", null)
 *     }
 * }
 *
 * // For weight streaming:
 * fun sendWeight(callbackName: String, weight: Double, unit: String, stable: Boolean) {
 *     val json = """{"weight":$weight,"unit":"$unit","stable":$stable}"""
 *     webView.post {
 *         webView.evaluateJavascript("window.$callbackName && window.$callbackName($json)", null)
 *     }
 * }
 * ```
 */

import { BtDeviceInfo, ScaleReading, DeviceType } from './bluetooth.service';

// ─── Bridge Type Definitions ─────────────────────────────────────────────────

/** Matches the interface injected by the Android app via addJavascriptInterface */
interface AndroidBluetoothNative {
    isBluetoothEnabled(): boolean;
    scanDevices(filter: string, callbackId: string): void;
    connectDevice(deviceId: string, deviceType: string, callbackId: string): void;
    disconnectDevice(deviceId: string): void;
    writePrinter(deviceId: string, base64Data: string, callbackId: string): void;
    startWeightStream(deviceId: string, callbackName: string): void;
    stopWeightStream(deviceId: string): void;
}

declare global {
    interface Window {
        AndroidBluetooth?: AndroidBluetoothNative;
        /** Generic callback handler invoked by native Android for async results */
        __btCallback: (callbackId: string, resultJson: string) => void;
        /** Weight streaming callbacks */
        [key: `__btWeight_${string}`]: (data: ScaleReading) => void;
    }
}

// ─── Bridge Implementation ───────────────────────────────────────────────────

class AndroidBluetoothBridge {
    private pendingCallbacks: Map<string, (result: any) => void> = new Map();
    private callbackIdCounter = 0;
    private weightStreamName: string | null = null;
    private onWeightChange: ((reading: ScaleReading) => void) | null = null;
    private onDisconnect: ((type: DeviceType) => void) | null = null;

    constructor() {
        // Install the global callback dispatcher that native Android calls back into.
        //
        // ⚠️ KEY: Kotlin's webView.evaluateJavascript("window.__btCallback('id', [...])")
        // passes the second argument as a real JavaScript value (array/object), NOT a string.
        // If resultJson is already an object/array, JSON.parse() would fail with
        // "map is not a function" or "Unexpected token". We must handle both cases.
        window.__btCallback = (callbackId: string, resultJson: any) => {
            const resolve = this.pendingCallbacks.get(callbackId);
            if (resolve) {
                this.pendingCallbacks.delete(callbackId);
                try {
                    // If native passes a string (e.g. from some bridge implementations), parse it.
                    // If it's already a JS object/array (Kotlin evaluateJavascript), use as-is.
                    const result = typeof resultJson === 'string'
                        ? JSON.parse(resultJson)
                        : resultJson;
                    resolve(result);
                } catch (e) {
                    resolve({ success: false, error: 'Bridge result error: ' + String(resultJson) });
                }
            }
        };
    }

    /** Returns true if the native Android Bluetooth interface is injected */
    isAvailable(): boolean {
        return typeof window !== 'undefined' && !!window.AndroidBluetooth;
    }

    /** Returns true if Android BT is actually turned on */
    isBluetoothEnabled(): boolean {
        if (!this.isAvailable()) return false;
        try {
            return window.AndroidBluetooth!.isBluetoothEnabled();
        } catch {
            return false;
        }
    }

    /** Generates a unique callback ID */
    private nextId(): string {
        return `bt_${++this.callbackIdCounter}_${Date.now()}`;
    }

    /** Wraps a native call that uses callbackId pattern into a Promise */
    private nativeCall<T>(fn: (callbackId: string) => void, timeoutMs = 30000): Promise<T> {
        return new Promise((resolve, reject) => {
            const id = this.nextId();
            const timer = setTimeout(() => {
                this.pendingCallbacks.delete(id);
                reject(new Error('蓝牙操作超时，请重试'));
            }, timeoutMs);

            this.pendingCallbacks.set(id, (result: T) => {
                clearTimeout(timer);
                resolve(result);
            });

            try {
                fn(id);
            } catch (err: any) {
                clearTimeout(timer);
                this.pendingCallbacks.delete(id);
                reject(new Error('调用蓝牙接口失败: ' + (err?.message || String(err))));
            }
        });
    }

    /** Scan for nearby BLE devices.
     * @param type filter: 'scale' | 'printer' | 'all'
     * @param onDeviceFound optional callback fired immediately for each discovered device (real-time streaming)
     */
    async scanDevices(
        type: DeviceType | 'all' = 'all',
        onDeviceFound?: (device: BtDeviceInfo) => void
    ): Promise<BtDeviceInfo[]> {
        return new Promise<BtDeviceInfo[]>((resolve, reject) => {
            const id = this.nextId();
            const safeId = id.replace(/[^a-zA-Z0-9_]/g, '_');

            // Register streaming handler: native calls window.__btScanUpdate_<safeId>(deviceJson)
            if (onDeviceFound) {
                (window as any)[`__btScanUpdate_${safeId}`] = (raw: any) => {
                    try {
                        const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        onDeviceFound({
                            id: d.id || d.address || d.mac,
                            name: d.name || '未知设备',
                            type: (d.type as DeviceType) || type as DeviceType,
                            connected: false,
                            rssi: d.rssi,
                        });
                    } catch { /* ignore parse errors */ }
                };
            }

            const cleanup = () => {
                delete (window as any)[`__btScanUpdate_${safeId}`];
                this.pendingCallbacks.delete(id);
            };

            const timer = setTimeout(() => {
                cleanup();
                reject(new Error('蓝牙操作超时，请重试'));
            }, 20000);

            this.pendingCallbacks.set(id, (result: any) => {
                clearTimeout(timer);
                cleanup();
                try {
                    const arr = typeof result === 'string' ? JSON.parse(result) : result;
                    resolve((arr || []).map((d: any) => ({
                        id: d.id || d.address || d.mac,
                        name: d.name || '未知设备',
                        type: (d.type as DeviceType) || type as DeviceType,
                        connected: false,
                        rssi: d.rssi,
                    })));
                } catch {
                    resolve([]);
                }
            });

            try {
                window.AndroidBluetooth!.scanDevices(type, id);
            } catch (err: any) {
                clearTimeout(timer);
                cleanup();
                reject(new Error('调用蓝牙接口失败: ' + (err?.message || String(err))));
            }
        });
    }


    /** Connect to a BLE device by its ID/MAC address */
    async connectDevice(deviceId: string, type: DeviceType): Promise<BtDeviceInfo> {
        const result = await this.nativeCall<any>(
            (id) => window.AndroidBluetooth!.connectDevice(deviceId, type, id),
            15000
        );

        if (!result?.success) {
            throw new Error(result?.error || '连接失败');
        }

        return {
            id: result.id || deviceId,
            name: result.name || deviceId,
            type,
            connected: true,
        };
    }

    /** Disconnect a device */
    disconnectDevice(deviceId: string): void {
        try {
            window.AndroidBluetooth?.disconnectDevice(deviceId);
        } catch (err) {
            console.warn('[AndroidBT] disconnectDevice error:', err);
        }
    }

    /** Send raw bytes to printer (as Base64) */
    async writePrinter(deviceId: string, data: Uint8Array): Promise<void> {
        // Convert Uint8Array to Base64
        const base64 = this.uint8ToBase64(data);

        const result = await this.nativeCall<any>(
            (id) => window.AndroidBluetooth!.writePrinter(deviceId, base64, id),
            60000 // Printing can take a while
        );

        if (!result?.success) {
            throw new Error(result?.error || '打印失败');
        }
    }

    /** Start streaming weight readings from a connected scale */
    startWeightStream(deviceId: string, onWeight: (reading: ScaleReading) => void): void {
        const callbackName = `__btWeight_${deviceId.replace(/[^a-zA-Z0-9]/g, '_')}`;
        this.weightStreamName = callbackName;

        // Install the global weight callback
        (window as any)[callbackName] = (data: any) => {
            const reading: ScaleReading = {
                weight: typeof data.weight === 'number' ? data.weight : parseFloat(data.weight),
                unit: data.unit === 'lb' ? 'lb' : 'kg',
                stable: !!data.stable,
                timestamp: Date.now(),
            };
            if (reading.weight > 0) {
                onWeight(reading);
                this.onWeightChange?.(reading);
            }
        };

        try {
            window.AndroidBluetooth?.startWeightStream(deviceId, callbackName);
        } catch (err) {
            console.warn('[AndroidBT] startWeightStream error:', err);
        }
    }

    /** Stop streaming weight readings */
    stopWeightStream(deviceId: string): void {
        if (this.weightStreamName) {
            try {
                window.AndroidBluetooth?.stopWeightStream(deviceId);
            } catch (err) {
                console.warn('[AndroidBT] stopWeightStream error:', err);
            }
            // Clean up global callback
            delete (window as any)[this.weightStreamName];
            this.weightStreamName = null;
        }
    }

    /** Register weight change callback (for use by bluetooth.service.ts) */
    setOnWeightChange(cb: (reading: ScaleReading) => void): void {
        this.onWeightChange = cb;
    }

    /** Register disconnect callback */
    setOnDisconnect(cb: (type: DeviceType) => void): void {
        this.onDisconnect = cb;
    }

    /** Trigger a disconnect event (called when native detects disconnection) */
    notifyDisconnect(type: DeviceType): void {
        this.onDisconnect?.(type);
    }

    /** Convert Uint8Array to Base64 string */
    private uint8ToBase64(bytes: Uint8Array): string {
        let binary = '';
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }
}

export const androidBluetoothBridge = new AndroidBluetoothBridge();
